import { useMemo } from 'react'
import {
  Button,
  Card,
  DataTable,
  DivergingBars,
  Donut,
  Kpi,
  Legend,
  PageHead,
  Pager,
  RankedBars,
  Skeleton,
  StateBlock,
  Waterfall,
  cumulative,
  seriesColor,
  toneColor,
  type Column,
  type WaterfallStep,
} from '@w7/ui'
import { useMemberPnl, usePnl, useSession } from '@/app/queries'
import { previousRange } from '@/app/range'
import { AccountLink, Amount, AsOf, MiniSpark, Money, deltaOf, exportCsv } from '@/app/bits'
import { round2 } from '@/lib/book'
import { EMPTY, count, dayLabel, lll, money, moneyCompact, percent } from '@/lib/format'
import { ROLE_LABEL, roleDepth, type GameBucket, type PnlRow, type Role, type Sport } from '@/data/types'
import {
  FilteredEmpty,
  ReportFilters,
  ScopeBanner,
  ScopeUnavailable,
  scopeSuffix,
  scopedCsvName,
  useReportFilters,
  type Dimension,
} from './ReportFilters'

/**
 * My P&L — replaces "master-dashboard-new".
 *
 * The original printed Win / Commission / NetAmount three times across one row,
 * with nothing anywhere saying what the three triples were (they were the
 * member's figure, the upline's and the caller's), plus separate "U/L
 * Commission" and "D/L Commission" columns and a Grand Total. Nine money columns
 * and not one chart. This screen keeps every one of those numbers but names them
 * once each, and puts the thing the triples were trying to express — how a
 * settled amount is divided down the chain — into a waterfall where it is
 * readable in a second.
 */

/** Table page size. Small enough that the sticky totals row stays on screen. */
const PAGE_SIZE = 25

/**
 * One page big enough to hold any range this console offers, used for the
 * product-mix charts. `PnlSummary` has no byGame/bySport field, so the mix has
 * to be derived from the rows; when the API grows one, this query goes away and
 * nothing else on the screen changes.
 */
const ALL_ROWS = 100_000

/**
 * Role and direction are absent, and that is a statement rather than an
 * oversight. Every row of `PnlSummary.rows` is member-grain (`memberId`), so
 * there is no agent-tier row to filter — filtering "by tier" would mean
 * filtering by the member's PARENT's tier, which is exactly what Scope does and
 * does better. A settled P&L row has no give/take direction at all.
 */
const DIMENSIONS: Dimension[] = ['range', 'scope', 'sport', 'game', 'member', 'q']

const ZERO_TOTALS = { turnover: 0, memberPnl: 0, commission: 0, net: 0 }

const sumRows = (rows: PnlRow[]) =>
  rows.reduce(
    (acc, row) => ({
      turnover: round2(acc.turnover + row.turnover),
      memberPnl: round2(acc.memberPnl + row.memberPnl),
      commission: round2(acc.commission + row.commission),
      net: round2(acc.net - row.netAmount),
    }),
    ZERO_TOTALS,
  )

export function MyPnlPage() {
  const filters = useReportFilters(DIMENSIONS)
  const { values, scopeAccount } = filters
  const range = values.range
  const { data: session } = useSession()

  const page = values.page

  const dates = useMemo(() => ({ from: range.from, to: range.to }), [range.from, range.to])
  const prior = useMemo(() => previousRange(range), [range])
  /* The ladder role the split is drawn against. Under a scope it must be the
     SCOPED agent's tier: the closing bar of the waterfall is labelled with it,
     and labelling their book with your tier is exactly the kind of quiet lie a
     provenance banner cannot undo. */
  const myRole = scopeAccount?.role ?? session?.session.role

  /* Every dimension the query can honour goes INTO the query, not into a
     post-filter: the KPI strip, the charts and the table then describe the same
     set of rows, which is the whole reason the bar exists. */
  const scoped = useMemo(
    () => ({
      ...(values.scope ? { rootId: values.scope } : {}),
      ...(values.sports.length ? { sports: values.sports } : {}),
      ...(values.games.length ? { games: values.games } : {}),
      ...(values.members.length ? { memberIds: values.members } : {}),
    }),
    [values.games, values.members, values.scope, values.sports],
  )

  /* Rows are read whole and paged HERE, not by the API.
     The mix charts already needed every row in the range, and the text filter
     is client-side over event and market names — server paging on top of a
     client filter would hand back pages of mostly-excluded rows and a page
     count that meant nothing. `summary` is left to carry only the range-wide
     aggregates: totals, the daily series and the hierarchy split. */
  const summary = usePnl({ range: dates, ...scoped, pageSize: 1, myRole })
  const breakdown = usePnl({ range: dates, ...scoped, pageSize: ALL_ROWS, myRole })
  /* Only `totals` is read from this one — hence a single row. */
  const previous = usePnl({ range: prior, ...scoped, pageSize: 1, myRole })
  /* A PnlRow carries `memberId` and no username. The member leaderboard is
     scoped to exactly the members that settled in this range, so it is the
     cheapest place to get names for the Member column. It is deliberately NOT
     narrowed by sport or game — a name map with holes in it renders an em dash
     where a member's username belongs. */
  const members = useMemberPnl({
    range: dates,
    ...(values.scope ? { rootId: values.scope } : {}),
  })

  const memberNames = useMemo(() => {
    const map = new Map<string, string>()
    for (const row of members.data?.rows ?? []) map.set(row.account.id, row.account.username)
    return map
  }, [members.data])

  const series = useMemo(() => {
    const daily = summary.data?.daily
    if (!daily) return null
    const net = daily.map((d) => d.netPnl)
    return {
      categories: daily.map((d) => dayLabel(d.date)),
      net,
      cumulativeNet: cumulative(net),
      turnover: daily.map((d) => d.turnover),
      days: daily.length,
    }
  }, [summary.data])

  /**
   * The hierarchy split.
   *
   * The whole settled net enters on the left, every other level in the ladder
   * takes its share percentage, and the closing bar is what lands with the
   * signed-in agent. Levels are ordered top of the ladder first so the sequence
   * reads the way the hierarchy does.
   */
  const split = useMemo(() => {
    const rows = summary.data?.splitByRole
    const totals = summary.data?.totals
    if (!rows || !totals || !myRole || rows.length === 0) return null

    const mine = rows.find((r) => r.role === myRole)?.amount ?? 0
    const others = rows
      .filter((r) => r.role !== myRole)
      .sort((a, b) => roleDepth(a.role) - roleDepth(b.role))

    const colorByRole = new Map<Role, string>()
    // seriesColor(0) is reserved for the closing bar, which the kit paints c1.
    others.forEach((r, i) => colorByRole.set(r.role, seriesColor(i + 1)))
    colorByRole.set(myRole, seriesColor(0))

    const steps: WaterfallStep[] = [
      { label: 'Settled net', value: totals.netPnl, color: toneColor(totals.netPnl) },
      ...others.map((r) => ({
        label: ROLE_LABEL[r.role],
        value: -r.amount,
        color: colorByRole.get(r.role),
      })),
      { label: ROLE_LABEL[myRole], value: mine, isTotal: true },
    ]

    const legend = [...rows]
      .sort((a, b) => roleDepth(a.role) - roleDepth(b.role))
      .map((r) => ({
        // "yours" is only true on your own seat; under a scope the closing bar
        // belongs to the agent named in the banner, not to the reader.
        label:
          r.role === myRole
            ? `${ROLE_LABEL[r.role]} — ${scopeAccount ? scopeAccount.username : 'yours'}`
            : ROLE_LABEL[r.role],
        color: colorByRole.get(r.role) ?? seriesColor(0),
        value: `${moneyCompact(r.amount)} · ${percent(totals.netPnl === 0 ? null : r.amount / totals.netPnl, 1)}`,
      }))

    return { steps, legend, mine, myRole }
  }, [myRole, scopeAccount, summary.data])

  const byGame = useMemo(() => {
    const rows = breakdown.data?.rows
    if (!rows) return null
    const map = new Map<GameBucket, number>()
    for (const row of rows) map.set(row.game, round2((map.get(row.game) ?? 0) - row.netAmount))
    return [...map.entries()]
      .map(([game, net]) => ({ game, net }))
      .sort((a, b) => b.net - a.net)
  }, [breakdown.data])

  const bySport = useMemo(() => {
    const rows = breakdown.data?.rows
    if (!rows) return null
    const map = new Map<Sport, number>()
    for (const row of rows) map.set(row.sport, round2((map.get(row.sport) ?? 0) - row.netAmount))
    return [...map.entries()]
      .map(([sport, net]) => ({ sport, net }))
      .sort((a, b) => b.net - a.net)
  }, [breakdown.data])

  const totals = summary.data?.totals
  const previousTotals = previous.data?.totals

  /* The text filter is the one dimension the query cannot honour — `PnlQuery`
     has no free-text field and inventing one would mean a param whose meaning
     differs from the same param everywhere else. It runs over event and market
     names on rows already in hand, which is exactly as far as it can honestly
     reach; the label on the box says so. */
  const needle = values.q.trim().toLowerCase()
  const rows = useMemo(() => {
    const all = breakdown.data?.rows ?? []
    if (!needle) return all
    return all.filter(
      (row) =>
        row.eventName.toLowerCase().includes(needle) || row.marketName.toLowerCase().includes(needle),
    )
  }, [breakdown.data, needle])

  /* A totals row must describe the rows its table is willing to show. Without a
     text filter that is the whole range, and the range figures come from the
     API rather than a re-add of the page. */
  const shown = useMemo(() => (needle ? sumRows(rows) : null), [needle, rows])

  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE))
  // A remembered page can point past the end once a filter narrows the set.
  const safePage = Math.min(page, pageCount)
  const pageRows = rows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE)

  const columns = useMemo<Column<PnlRow>[]>(
    () => [
      {
        id: 'settled',
        header: 'Settled',
        cell: (row) => <span className="w7-dim">{lll(row.settledAt)}</span>,
        sortValue: (row) => row.settledAt,
        width: 172,
        total: <span className="w7-dim">{shown ? 'Matching rows' : 'Whole range'}</span>,
      },
      {
        id: 'event',
        header: 'Event',
        cell: (row) => (
          <span className="w7-truncate" style={{ display: 'block', maxWidth: 240 }} title={row.eventName}>
            {row.eventName}
          </span>
        ),
        sortValue: (row) => row.eventName,
      },
      {
        id: 'market',
        header: 'Market',
        cell: (row) => (
          <span className="w7-truncate" style={{ display: 'block', maxWidth: 200 }} title={row.marketName}>
            {row.marketName}
          </span>
        ),
        sortValue: (row) => row.marketName,
        hideBelow: 1500,
      },
      {
        id: 'game',
        header: 'Game',
        cell: (row) => <span className="w7-dim">{row.game}</span>,
        sortValue: (row) => row.game,
        hideBelow: 1280,
      },
      {
        id: 'member',
        header: 'Member',
        cell: (row) => {
          const username = memberNames.get(row.memberId)
          return username ? <AccountLink id={row.memberId} username={username} /> : EMPTY
        },
        sortValue: (row) => memberNames.get(row.memberId) ?? null,
        hideBelow: 900,
      },
      {
        id: 'turnover',
        header: 'Turnover',
        numeric: true,
        cell: (row) => <Amount value={row.turnover} />,
        sortValue: (row) => row.turnover,
        total: <Amount value={shown ? shown.turnover : totals?.turnover} />,
        hideBelow: 1080,
      },
      {
        id: 'member-pnl',
        header: 'Member P&L',
        numeric: true,
        help: 'The member’s own result. Negative means the member lost, which is a win for the book — so it is coloured by the book’s outcome, not by its own sign.',
        // `invert` is the whole point: this console colours by what happened to
        // the book, and a member losing money is the book winning it.
        cell: (row) => <Money value={row.memberPnl} signed invert />,
        sortValue: (row) => row.memberPnl,
        total: <Money value={shown ? shown.memberPnl : totals?.memberPnl} signed invert />,
      },
      {
        id: 'commission',
        header: 'Commission',
        numeric: true,
        help: 'Charged on winning bets and already netted out of the row’s net.',
        cell: (row) => <Amount value={row.commission} />,
        sortValue: (row) => row.commission,
        total: <Amount value={shown ? shown.commission : totals?.commission} />,
        hideBelow: 1080,
      },
      {
        id: 'net',
        header: 'Net to book',
        numeric: true,
        help: 'Member P&L after commission, from the book’s side. Positive means the book kept it.',
        cell: (row) => <Money value={-row.netAmount} signed />,
        sortValue: (row) => -row.netAmount,
        total: <Money value={shown ? shown.net : totals?.netPnl} signed />,
      },
    ],
    [memberNames, shown, totals],
  )


  const account = session?.account
  const netDelta = totals && previousTotals ? deltaOf(totals.netPnl, previousTotals.netPnl) : null
  const turnoverDelta = totals && previousTotals ? deltaOf(totals.turnover, previousTotals.turnover) : null
  const commissionDelta =
    totals && previousTotals ? deltaOf(totals.commission, previousTotals.commission) : null
  const marginDelta = totals && previousTotals ? deltaOf(totals.margin, previousTotals.margin) : null
  const betsDelta = totals && previousTotals ? deltaOf(totals.bets, previousTotals.bets) : null
  const membersDelta = totals && previousTotals ? deltaOf(totals.members, previousTotals.members) : null

  // The file carries exactly what is on screen, filters and all — a CSV that
  // silently held more rows than the table would be a different report.
  const exportRows = rows
  const onExport = () =>
    exportCsv(
      scopedCsvName('my-pnl', scopeAccount, range),
      // Grouped Indian format rather than raw floats: the file is opened by a
      // human next to this screen, and the two must agree digit for digit.
      exportRows.map((row) => ({
        Settled: lll(row.settledAt),
        Event: row.eventName,
        Market: row.marketName,
        Game: row.game,
        Sport: row.sport,
        Member: memberNames.get(row.memberId) ?? EMPTY,
        Turnover: money(row.turnover, { symbol: false }),
        'Member P&L': money(row.memberPnl, { symbol: false, signed: true }),
        Commission: money(row.commission, { symbol: false }),
        'Net to book': money(-row.netAmount, { symbol: false, signed: true }),
      })),
    )

  const head = (
    <PageHead
      title="My P&L"
      subtitle={`${account ? `${account.displayName || account.username} · ${ROLE_LABEL[account.role]}` : EMPTY} · ${range.label}${scopeSuffix(scopeAccount)}`}
    />
  )

  /* A scope from a pasted URL that is not beneath you. Nothing on this page is
     safe to draw — not even a zero — because a zero under their name is still a
     claim about their book. */
  if (filters.scopeInvalid) {
    return (
      <>
        {head}
        <ScopeUnavailable onClear={() => filters.clearDimension('scope')} />
      </>
    )
  }

  return (
    <>
      {head}

      <ReportFilters
        state={filters}
        memberSource="settled"
        textLabel="Filter events and markets"
        textPlaceholder="Event or market"
        omission="Tier and direction are not offered here: every settled row belongs to one member, so a tier filter would mean filtering by their agent’s tier — which is what Viewing as does, and does better — and a settled row has no give or take direction."
        actions={
          <>
            <AsOf at={summary.dataUpdatedAt || undefined} />
            <Button
              icon="download"
              variant="secondary"
              onClick={onExport}
              disabled={exportRows.length === 0}
            >
              Export CSV
            </Button>
          </>
        }
      />

      {scopeAccount ? (
        <ScopeBanner account={scopeAccount} onClear={() => filters.clearDimension('scope')} />
      ) : null}

      {/* ---------------------------------------------------------- KPIs -- */}
      <div className="w7-grid w7-grid-6" style={{ marginBottom: 'var(--w7-s5)' }}>
        <Kpi
          label="Net P&L"
          value={totals ? moneyCompact(totals.netPnl) : EMPTY}
          exact={money(totals?.netPnl)}
          tone={totals && totals.netPnl < 0 ? 'loss' : 'profit'}
          delta={netDelta?.text}
          deltaTone={netDelta?.tone}
          spark={series ? <MiniSpark values={series.cumulativeNet} /> : undefined}
          accent
          meta={
            split
              ? `Across the whole downline · ${money(split.mine, { round: true })} of it reaches ${scopeAccount ? scopeAccount.username : 'you'}`
              : 'Across the whole downline'
          }
        />
        <Kpi
          label="Turnover"
          value={totals ? moneyCompact(totals.turnover) : EMPTY}
          exact={money(totals?.turnover)}
          delta={turnoverDelta?.text}
          deltaTone={turnoverDelta?.tone}
          spark={series ? <MiniSpark values={series.turnover} /> : undefined}
          meta="Matched volume on the markets that settled"
        />
        <Kpi
          label="Commission"
          value={totals ? moneyCompact(totals.commission) : EMPTY}
          exact={money(totals?.commission)}
          delta={commissionDelta?.text}
          // Commission moving either way is neither a win nor a loss on its own,
          // so the change is stated without a colour verdict.
          deltaTone="neutral"
          meta="Charged on winning bets, already netted out of the figures here"
        />
        <Kpi
          label="Margin"
          value={totals ? percent(totals.margin, 2) : EMPTY}
          tone={totals && totals.margin < 0 ? 'loss' : 'profit'}
          delta={marginDelta?.text}
          deltaTone={marginDelta?.tone}
          meta="Net P&L over turnover — the share of matched volume the book actually held"
        />
        <Kpi
          label="Settled bets"
          value={totals ? count(totals.bets) : EMPTY}
          delta={betsDelta?.text}
          deltaTone={betsDelta?.tone}
          meta={
            series ? `Settled on ${count(series.days)} of ${count(range.days)} days` : undefined
          }
        />
        <Kpi
          label="Members who settled"
          value={totals ? count(totals.members) : EMPTY}
          delta={membersDelta?.text}
          deltaTone={membersDelta?.tone}
          meta="Members with at least one market settled in this period"
        />
      </div>

      {/* ------------------------------------------------------ headline -- */}
      <Card
        title="Daily net P&L"
        subtitle="Bars are each day on its own; the line is the running total across the period. Green above the line is a day the book won, red below is a day it paid out."
        style={{ marginBottom: 'var(--w7-s5)' }}
      >
        {series ? (
          <DivergingBars
            label="Net profit and loss by day, with the running total"
            categories={series.categories}
            values={series.net}
            cumulativeValues={series.cumulativeNet}
            height={260}
            format={(v) => moneyCompact(v, false)}
            maxLabels={8}
            valueLabel="Net that day"
            cumulativeLabel="Running total"
            emptyMessage="Nothing settled in this period"
          />
        ) : (
          <ChartSkeleton height={260} />
        )}
      </Card>

      {/* --------------------------------------------------------- split -- */}
      <Card
        title="Where the settled net goes"
        subtitle="Each level of the ladder takes its share percentage of every settled amount, and the closing bar is what reaches you. Shares are normalised, so the parts always add back to the whole — no rounding residue disappears between levels."
        style={{ marginBottom: 'var(--w7-s5)' }}
      >
        {split ? (
          <>
            <Waterfall
              label="How the settled net divides across the hierarchy"
              steps={split.steps}
              height={240}
              format={(v) => moneyCompact(v, false)}
              emptyMessage="Nothing settled in this period"
            />
            <Legend items={split.legend} />
          </>
        ) : summary.isLoading ? (
          <ChartSkeleton height={240} />
        ) : summary.data?.earningsUnavailable ? (
          <StateBlock icon="info" title="Your cut is not available yet">
            {summary.data.earningsUnavailable.reason}
            {summary.data.earningsUnavailable.trackedBy
              ? ` Tracked by ${summary.data.earningsUnavailable.trackedBy}.`
              : null}{' '}
            Book net above is from settlement marts; personal earnings wait for ledger postings (never
            invented from share %).
          </StateBlock>
        ) : (
          <StateBlock icon="pnl" title="Nothing has settled in this period">
            Once a market one of the members beneath this seat bet on settles, this shows how the
            amount is divided down the ladder and how much of it lands with a{' '}
            {myRole ? ROLE_LABEL[myRole] : 'n agent'}.
          </StateBlock>
        )}
      </Card>

      {/* ----------------------------------------------------- mix charts -- */}
      <div className="w7-grid w7-grid-2" style={{ marginBottom: 'var(--w7-s5)' }}>
        <Card
          title="Net P&L by game"
          subtitle="Ring segments are the buckets the book won on. A bucket the book paid out on cannot size a segment, so it is listed below with a minus."
        >
          {byGame ? (
            <>
              <Donut
                label="Net profit and loss by game bucket"
                height={200}
                // Colours are pinned to the bucket, not to the ring's position:
                // the ring drops the buckets the book lost on, so an implicit
                // index would slide the legend's swatches off their buckets.
                slices={byGame.map((g, i) => ({ label: g.game, value: g.net, color: seriesColor(i) }))}
                format={(v) => money(v, { round: true })}
                centerValue={totals ? moneyCompact(totals.netPnl) : EMPTY}
                centerLabel="net"
                emptyMessage="No game bucket finished ahead in this period"
              />
              <Legend
                items={byGame.map((g, i) => ({
                  label: g.game,
                  color: seriesColor(i),
                  value: moneyCompact(g.net),
                }))}
              />
            </>
          ) : (
            <ChartSkeleton height={200} />
          )}
        </Card>

        <Card
          title="Net P&L by sport"
          subtitle="Green is a sport the book won on, red is one it paid out on"
        >
          {bySport ? (
            <RankedBars
              label="Net profit and loss by sport"
              rows={bySport.map((s) => ({ label: s.sport, value: s.net }))}
              format={(v) => moneyCompact(v)}
              byTone
              emptyMessage="No sport has settled activity in this period"
            />
          ) : (
            <ChartSkeleton height={200} />
          )}
        </Card>
      </div>

      {/* ---------------------------------------------------------- rows -- */}
      <Card
        title="Settled rows"
        subtitle={`One row per settled market, newest first. Member P&L is the member’s own result — negative means the member lost, which is a win for the book, so it is coloured by the book’s outcome rather than by its own sign. The totals row covers ${shown ? 'every row matching the text filter' : 'the whole range'}; sorting reorders the rows on this page.`}
        flush
        footer={
          <Pager
            page={safePage}
            pageCount={pageCount}
            onChange={filters.setPage}
            total={rows.length}
            pageSize={PAGE_SIZE}
          />
        }
      >
        <DataTable
          label="Settled profit and loss rows"
          rows={pageRows}
          columns={columns}
          rowKey={(row) => row.id}
          loading={breakdown.isLoading}
          skeletonRows={12}
          showTotals={Boolean(totals)}
          totalsLabel={shown ? 'Matching rows' : undefined}
          empty={
            filters.isFiltered ? (
              <FilteredEmpty state={filters} icon="pnl" />
            ) : (
              <StateBlock icon="pnl" title="Nothing settled in this period">
                A row lands here when a market one of your members bet on settles. Widen the date
                range, or open Exposure to see the positions that are still open.
              </StateBlock>
            )
          }
        />
      </Card>
    </>
  )
}

/** Chart-sized loading block. Flex makes the kit's inline skeleton span fill it. */
function ChartSkeleton({ height }: { height: number }) {
  return (
    <div style={{ display: 'flex', height }} aria-hidden="true">
      <Skeleton height="100%" radius={10} />
    </div>
  )
}
